import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Play, Send, Loader2, CheckCircle, XCircle, ChevronDown, ChevronUp } from 'lucide-react'
import styles from './PythonReplActivity.module.css'

// ── Pyodide singleton ────────────────────────────────────────────
let pyodidePromise: Promise<any> | null = null

function loadPyodide(): Promise<any> {
  if (!pyodidePromise) {
    pyodidePromise = (async () => {
      const mod = await import(
        /* @vite-ignore */ 'https://cdn.jsdelivr.net/pyodide/v0.27.5/full/pyodide.mjs'
      )
      const pyodide = await mod.loadPyodide()
      return pyodide
    })()
  }
  return pyodidePromise
}

// ── Types ────────────────────────────────────────────────────────
interface TestResult {
  id: string
  description: string
  passed: boolean
  expected: string
  actual: string
}

interface PythonReplActivityProps {
  activityData: Record<string, any>
  disabled?: boolean
  hideSubmit?: boolean
  onSubmitResponse: (data: Record<string, any>) => Promise<{ success: boolean; error?: string }>
  onComplete?: () => void | Promise<void>
}

// ── Component ────────────────────────────────────────────────────
export function PythonReplActivity({ activityData, disabled, hideSubmit, onSubmitResponse, onComplete }: PythonReplActivityProps) {
  const data = activityData || {}
  const question: string = data.question || ''
  const starterCode: string = data.starter_code || '# Write your code here\n'
  const hints: string[] = data.hints || []
  const testCases: Array<{ id: string; input: string; expected_output: string; description: string }> =
    data.test_cases || []

  const [code, setCode] = useState(starterCode)
  const [output, setOutput] = useState('')
  const [testResults, setTestResults] = useState<TestResult[]>([])
  const [running, setRunning] = useState(false)
  const [pyodideReady, setPyodideReady] = useState(false)
  const [loadingPyodide, setLoadingPyodide] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [showHint, setShowHint] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Load Pyodide on mount
  useEffect(() => {
    setLoadingPyodide(true)
    loadPyodide()
      .then(() => setPyodideReady(true))
      .catch(() => setError('Failed to load Python runtime'))
      .finally(() => setLoadingPyodide(false))
  }, [])

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current
    if (ta) {
      ta.style.height = 'auto'
      ta.style.height = `${Math.max(ta.scrollHeight, 120)}px`
    }
  }, [code])

  // Handle Tab key → 4 spaces
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault()
      const ta = e.currentTarget
      const start = ta.selectionStart
      const end = ta.selectionEnd
      const newCode = code.substring(0, start) + '    ' + code.substring(end)
      setCode(newCode)
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = start + 4
      })
    }
  }, [code])

  // Run code against a single test case
  const runWithTestCase = async (pyodide: any, userCode: string, tc: { id: string; input: string; expected_output: string; description: string }): Promise<TestResult> => {
    let stdout = ''

    // Build stdin queue from test case input
    const inputLines = tc.input ? tc.input.split('\n') : []
    let inputIndex = 0

    // Set up I/O overrides
    pyodide.setStdout({ batched: (text: string) => { stdout += text + '\n' } })
    pyodide.setStderr({ batched: () => {} })
    pyodide.setStdin({
      stdin: () => {
        if (inputIndex < inputLines.length) {
          return inputLines[inputIndex++]
        }
        return ''
      }
    })

    try {
      // Run with 10-second timeout
      await Promise.race([
        pyodide.runPythonAsync(userCode),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Execution timed out (10s limit)')), 10000))
      ])
    } catch (err: any) {
      const errMsg = err.message || String(err)
      // If timeout, return fail
      if (errMsg.includes('timed out')) {
        return { id: tc.id, description: tc.description, passed: false, expected: tc.expected_output.trim(), actual: 'Timeout: code took too long to execute' }
      }
      // Python error — include in output
      return { id: tc.id, description: tc.description, passed: false, expected: tc.expected_output.trim(), actual: errMsg.split('\n').slice(-2).join('\n').trim() }
    }

    const actualOutput = stdout.trim()
    const expectedOutput = tc.expected_output.trim()
    return {
      id: tc.id,
      description: tc.description,
      passed: actualOutput === expectedOutput,
      expected: expectedOutput,
      actual: actualOutput
    }
  }

  const handleRun = async () => {
    if (!pyodideReady || running) return
    setRunning(true)
    setOutput('')
    setTestResults([])
    setError(null)

    try {
      const pyodide = await loadPyodide()

      // First, run the code once for output display (use first test case's input if available)
      let displayOutput = ''
      const firstTc = testCases[0]
      const inputLines = firstTc?.input ? firstTc.input.split('\n') : []
      let inputIdx = 0

      pyodide.setStdout({ batched: (text: string) => { displayOutput += text + '\n' } })
      pyodide.setStderr({ batched: (text: string) => { displayOutput += '[stderr] ' + text + '\n' } })
      pyodide.setStdin({
        stdin: () => {
          if (inputIdx < inputLines.length) return inputLines[inputIdx++]
          return ''
        }
      })

      try {
        await Promise.race([
          pyodide.runPythonAsync(code),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Execution timed out (10s limit)')), 10000))
        ])
      } catch (err: any) {
        const errMsg = err.message || String(err)
        if (errMsg.includes('timed out')) {
          displayOutput += '\n[Error] Execution timed out (10s limit)'
        } else {
          // Extract last meaningful Python error line
          displayOutput += '\n' + errMsg.split('\n').slice(-2).join('\n').trim()
        }
      }
      setOutput(displayOutput.trim())

      // Run test cases
      if (testCases.length > 0) {
        const results: TestResult[] = []
        for (const tc of testCases) {
          const result = await runWithTestCase(pyodide, code, tc)
          results.push(result)
        }
        setTestResults(results)
      }
    } catch (err: any) {
      setError(err.message || 'Unknown error')
    } finally {
      setRunning(false)
    }
  }

  const handleSubmit = async () => {
    if (submitted || disabled) return

    const totalPassed = testResults.filter(r => r.passed).length
    const totalTests = testResults.length

    try {
      await onSubmitResponse({
        code,
        test_results: testResults.map(r => ({ id: r.id, passed: r.passed, description: r.description })),
        total_passed: totalPassed,
        total_tests: totalTests,
        output: output
      })
      setSubmitted(true)
    } catch {
      // submission failed
    }
  }

  // Line numbers
  const lineCount = code.split('\n').length

  if (!hideSubmit && (submitted || (disabled && !submitted))) {
    // Show results with correct answers after submission (self-paced)
    if (submitted && onComplete && testResults.length > 0) {
      const passed = testResults.filter(r => r.passed).length
      const allPassed = passed === testResults.length
      return (
        <div className="p-2 sm:p-4 space-y-4">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl ${allPassed ? 'bg-[var(--color-success-subtle)] border border-[var(--color-success)]/30' : 'bg-[var(--color-danger-subtle)] border border-[var(--color-danger)]/30'}`}>
            {allPassed
              ? <CheckCircle className="w-5 h-5 text-[var(--color-success)] shrink-0" />
              : <XCircle className="w-5 h-5 text-[var(--color-danger)] shrink-0" />
            }
            <span className={`font-semibold text-sm ${allPassed ? 'text-[var(--color-success)]' : 'text-[var(--color-danger)]'}`}>
              {allPassed ? 'All tests passed!' : `${passed}/${testResults.length} tests passed`}
            </span>
          </div>

          <div className="space-y-2">
            {testResults.map((r) => (
              <div key={r.id} className={`flex items-start gap-3 p-3 rounded-lg border ${
                r.passed
                  ? 'bg-[var(--color-success-subtle)] border-[var(--color-success)]/30'
                  : 'bg-[var(--color-danger-subtle)] border-[var(--color-danger)]/30'
              }`}>
                {r.passed
                  ? <CheckCircle className="w-5 h-5 text-[var(--color-success)] shrink-0 mt-0.5" />
                  : <XCircle className="w-5 h-5 text-[var(--color-danger)] shrink-0 mt-0.5" />
                }
                <div className="flex-1">
                  <p className="text-sm font-medium text-theme-text">{r.description}</p>
                  <p className="text-xs text-theme-muted mt-1">
                    Expected: <code className="bg-[var(--color-surface-hover)] px-1 rounded">{r.expected}</code>
                    {!r.passed && (
                      <> — Got: <code className="bg-[var(--color-surface-hover)] px-1 rounded">{r.actual || '(empty)'}</code></>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={() => onComplete()}
            className="w-full py-3 rounded-lg font-semibold text-sm transition-all hover:opacity-90 text-[var(--color-on-primary)]"
            style={{ background: 'var(--gradient-primary)' }}
          >
            Start next activity →
          </button>
        </div>
      )
    }

    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-subtle border border-theme-primary flex items-center justify-center">
          <svg className="w-8 h-8 text-theme-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-theme-primary mb-2">Waiting for Next Activity</h3>
        <p className="text-theme-text text-sm">
          {testResults.length > 0
            ? `${testResults.filter(r => r.passed).length}/${testResults.length} tests passed`
            : 'Your captain will start the next activity soon.'}
        </p>
      </div>
    )
  }

  const runDisabled = running || !pyodideReady
  const submitDisabled = testResults.length === 0

  return (
    <div className={styles.container}>
      {/* Problem statement */}
      <div className={styles.problemSection}>
        <p className={styles.questionText}>{question}</p>
        {hints.length > 0 && (
          <div className={styles.hintWrapper}>
            <button
              onClick={() => setShowHint(!showHint)}
              className={styles.hintToggle}
            >
              {showHint ? <ChevronUp className={styles.hintChevron} /> : <ChevronDown className={styles.hintChevron} />}
              {showHint ? 'Hide hint' : 'Show hint'}
            </button>
            {showHint && (
              <p className={styles.hintText}>
                {hints[0]}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Code editor */}
      <div className={styles.editorWrap}>
        {/* Line numbers */}
        <div className={styles.lineNumbers}>
          {Array.from({ length: lineCount }, (_, i) => (
            <div key={i}>{i + 1}</div>
          ))}
        </div>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={handleKeyDown}
          spellCheck={false}
          className={styles.codeTextarea}
        />
      </div>

      {/* Action buttons */}
      <div className={styles.actionBar}>
        <button
          onClick={handleRun}
          disabled={runDisabled}
          className={runDisabled ? styles.runBtnDisabled : styles.runBtn}
        >
          {running ? (
            <><Loader2 className={`${styles.iconSm} animate-spin`} /> Running...</>
          ) : loadingPyodide ? (
            <><Loader2 className={`${styles.iconSm} animate-spin`} /> Loading Python...</>
          ) : (
            <><Play className={styles.iconSm} /> Run Code</>
          )}
        </button>

        {!hideSubmit && (
          <button
            onClick={handleSubmit}
            disabled={submitDisabled}
            className={submitDisabled ? styles.submitBtnDisabled : styles.submitBtn}
          >
            <Send className={styles.iconSm} /> Submit
          </button>
        )}
      </div>

      {/* Output */}
      {(output || error) && (
        <div className={styles.outputSection}>
          <div className={styles.outputLabel}>
            <span>Output</span>
          </div>
          <pre className={styles.outputPre} data-error={error ? 'true' : undefined}>
            {error || output}
          </pre>
        </div>
      )}

      {/* Test results */}
      {testResults.length > 0 && (
        <div>
          <div className={styles.testLabel}>
            <span>
              Test Results — {testResults.filter(r => r.passed).length}/{testResults.length} passed
            </span>
          </div>
          <div className={styles.testSection}>
            {testResults.map((r) => (
              <div key={r.id} className={styles.testRow}>
                {r.passed ? (
                  <CheckCircle className={styles.testIcon} data-passed="true" />
                ) : (
                  <XCircle className={styles.testIcon} data-passed="false" />
                )}
                <div className={styles.testDesc}>
                  <div className={r.passed ? styles.testDescPass : styles.testDescFail}>
                    {r.description}
                  </div>
                  {!r.passed && (
                    <div className={styles.testExpected}>
                      Expected: <span className={styles.expectedValue}>{r.expected}</span>
                      <br />
                      Got: <span className={styles.gotValue}>{r.actual || '(no output)'}</span>
                    </div>
                  )}
                </div>
                <span className={r.passed ? styles.badgePass : styles.badgeFail}>
                  {r.passed ? 'PASS' : 'FAIL'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
